import { makeAutoObservable } from 'mobx'
import { getCDNAssetURL } from 'shared/lib'
import { Attachment } from 'entities/Attachment/model/Attachment'
import { ReachableUrlStore } from 'entities/Compliance/ui/ReachableUrl'
import { type PresentationModeStore } from 'widgets/PresentationMode'
import { type ComplianceLayerStore } from 'pages/settings/pages/compliance/store/complianceLayerStore'

export class CommonOptInContentStore {
  private _onlineFormUrl = ''
  private _needCheckUrlAfterChange = false
  private _isCorrectOnlineFormUrl = false
  private _noPublishedOnlineFormUrl = false
  private _termsAndPrivacyAgreement = false
  private _similarityDomainsWarning = ''

  readonly reachableOnlineFormUrlUrlStore = new ReachableUrlStore({
    errorText: 'Please enter a valid url (e.g. https://www.example.com/)',
    warningText:
      "Couldn't verify this link automatically. You can still continue — make sure it opens publicly.",
  })

  constructor(
    private _complianceLayerStore: ComplianceLayerStore,
    private _presentationStore: PresentationModeStore
  ) {
    makeAutoObservable(this)
  }

  get onlineFormUrl() {
    return this._onlineFormUrl
  }

  get isCorrectOnlineFormUrl() {
    return this._isCorrectOnlineFormUrl
  }

  get noPublishedOnlineFormUrl() {
    return this._noPublishedOnlineFormUrl
  }

  get termsAndPrivacyAgreement() {
    return this._termsAndPrivacyAgreement
  }

  get similarityDomainsWarning() {
    return this._similarityDomainsWarning
  }

  get needCheckUrlAfterChange() {
    return this._needCheckUrlAfterChange
  }

  setIsCorrectOnlineFormUrl = (isCorrect: boolean) => {
    this._isCorrectOnlineFormUrl = isCorrect
  }

  get isOnlineFormUrl() {
    return (
      (this.onlineFormUrl.length && !this.reachableOnlineFormUrlUrlStore.isError) ||
      this.noPublishedOnlineFormUrl
    )
  }

  get messageWarning() {
    if (this.reachableOnlineFormUrlUrlStore.isWarning) {
      return this.reachableOnlineFormUrlUrlStore.warningText
    }

    return this.similarityDomainsWarning
  }

  resetSimilarityDomainsWarningAddError = () => {
    this.setSimilarityDomainsWarning('')
  }

  checkUrlValidation = async (onlineFormUrl: string) => {
    this.setIsCorrectOnlineFormUrl(false)

    const result = await this.reachableOnlineFormUrlUrlStore.checkSingleUrl(onlineFormUrl)

    if (result.isError) {
      this.resetSimilarityDomainsWarningAddError()

      return
    }

    if (!this.noPublishedOnlineFormUrl) {
      this.setIsCorrectOnlineFormUrl(true)
    }

    const { website_url } = this._complianceLayerStore.businessInformation || {}

    if (!result.domainCheck && website_url) {
      this.setSimilarityDomainsWarning(
        "It looks like your Online Form URL domain doesn't match your business website domain"
      )
    }

    this._needCheckUrlAfterChange = false
  }

  setSimilarityDomainsWarning = (error: string) => {
    this._similarityDomainsWarning = error
  }

  checkEmail = (url: string) => {
    this.setSimilarityDomainsWarning('')
    this.reachableOnlineFormUrlUrlStore.dispose()
    this.checkUrlValidation(url)
  }

  setNoPublishedOnlineFormUrl = (value: boolean) => {
    this._noPublishedOnlineFormUrl = value
  }

  setOnlineFormUrl = (value: string, isCorrect = false) => {
    this.setIsCorrectOnlineFormUrl(isCorrect)
    this._needCheckUrlAfterChange = !isCorrect

    this._onlineFormUrl = value
  }

  handleNoPublishedOnlineFormUrl = (value: boolean) => {
    if (!this.noPublishedOnlineFormUrl) {
      this._onlineFormUrl = ''
      this._needCheckUrlAfterChange = false
      this.setIsCorrectOnlineFormUrl(false)
      this.reachableOnlineFormUrlUrlStore.dispose()
      this._similarityDomainsWarning = ''
    }

    this.setNoPublishedOnlineFormUrl(value)
  }

  setTermsAndPrivacyAgreement = () => {
    this._termsAndPrivacyAgreement = !this.termsAndPrivacyAgreement
  }

  openApprovedExampleModal = (urls: string[], name: string[]) => {
    const attachments = urls.map(
      (url, index) => new Attachment({ imageUrl: getCDNAssetURL(url), name: name[index] })
    )

    this._presentationStore.setAttachments(attachments)
  }
}
